// Header autocomplete search overlay.
// Docs: https://www.algolia.com/doc/ui-libraries/autocomplete/core-concepts/basic-configuration-options/

import algoliasearch from 'algoliasearch/lite';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';

import '../../scss/autocomplete.scss';

import config from '../config.js';
import { buildKicker } from '../kicker.js';

const MIN_QUERY_LENGTH = 3;

function searchPageUrl(query) {
  const sep = config.searchUrl.indexOf('?') === -1 ? '?' : '&';
  return `${config.searchUrl}${sep}keywords=${encodeURIComponent(query)}`;
}

/**
 * Reveal the overlay when the theme's existing search icon is clicked, without
 * touching the theme header: we intercept the label click and preventDefault so
 * the native `#search_menu` checkbox never toggles (its CSS-revealed form stays
 * hidden), then slide our overlay down instead.
 *
 * The overlay is a modal: focus is trapped across the input, the persistent
 * "View All Results" link, and the close button (results are ARIA options,
 * navigated with the arrow keys). Esc / backdrop / the X button close it and
 * return focus to the trigger.
 */
function initOverlay() {
  const overlay = document.getElementById('cornish-search-overlay');
  if (!overlay) {
    return null;
  }
  // From here on the overlay is JS-controlled via the `is-open` class (the
  // `hidden` attribute was only a pre-JS / no-JS fallback).
  overlay.hidden = false;

  const closeBtn = overlay.querySelector('.cornish-search-overlay__close');
  const viewAll = overlay.querySelector('.cornish-search-overlay__viewall');
  const header =
    document.querySelector('.header--main') || document.querySelector('header');
  const input = () => overlay.querySelector('.aa-Input');
  let lastTrigger = null;

  // Match the red band to the live header's height and colour so the overlay
  // reads as the header itself sliding down.
  function syncHeight() {
    if (!header) {
      return;
    }
    const h = Math.round(header.getBoundingClientRect().height);
    if (h) {
      overlay.style.setProperty('--cornish-header-h', `${h}px`);
    }
    const bg = getComputedStyle(header).backgroundColor;
    if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') {
      overlay.style.setProperty('--cornish-header-bg', bg);
    }
  }

  // Ordered focusables for the trap (results are options, not tabbable).
  function focusables() {
    return [input(), viewAll && !viewAll.hidden ? viewAll : null, closeBtn].filter(
      Boolean
    );
  }

  function open(trigger) {
    lastTrigger = trigger || null;
    syncHeight();
    overlay.classList.add('is-open');
    document.body.classList.add('cornish-search-open');
    window.requestAnimationFrame(() => {
      const el = input();
      if (el) {
        el.focus();
      }
    });
  }

  function close() {
    overlay.classList.remove('is-open');
    document.body.classList.remove('cornish-search-open');
    const el = input();
    if (el) {
      el.value = '';
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    const items = focusables();
    if (items.length < 2) {
      return;
    }
    const current = items.indexOf(document.activeElement);
    let next;
    if (event.shiftKey) {
      next = current <= 0 ? items.length - 1 : current - 1;
    } else {
      next = current === items.length - 1 ? 0 : current + 1;
    }
    event.preventDefault();
    items[next].focus();
  }

  // Hook every theme search toggle (label[for="search_menu"] etc.).
  document.querySelectorAll('.search_menu, [for="search_menu"]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      open(trigger);
    });
  });

  overlay.querySelectorAll('[data-search-close]').forEach((el) => {
    el.addEventListener('click', close);
  });
  overlay.addEventListener('keydown', onKeydown);
  window.addEventListener('resize', syncHeight);
  syncHeight();

  // "View All Results" navigation. Go on pointerdown, because a normal click
  // fires too late: the pointerdown blurs the input / registers as an outside
  // click, so Autocomplete closes the results panel and this button shifts up
  // out from under the pointer before mouseup — so the first click landed on
  // nothing. Keyboard (Enter) and modified/secondary clicks (open in new tab)
  // keep the anchor's default behaviour.
  if (viewAll) {
    viewAll.addEventListener('pointerdown', (event) => {
      if (
        event.pointerType === 'mouse' &&
        (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        return;
      }
      const href = viewAll.getAttribute('href');
      if (href) {
        event.preventDefault();
        window.location.href = href;
      }
    });
  }

  // Tabbing to the footer / close button blurs the input, and Autocomplete
  // closes the results on blur. Keep them visible: re-assert the panel open
  // whenever focus lands on those controls (as long as there's a query).
  let autocompleteApi = null;
  function keepResultsOpen() {
    const el = input();
    if (
      autocompleteApi &&
      typeof autocompleteApi.setIsOpen === 'function' &&
      el &&
      el.value.trim().length >= MIN_QUERY_LENGTH
    ) {
      autocompleteApi.setIsOpen(true);
    }
  }
  [viewAll, closeBtn].forEach((el) => {
    if (el) {
      el.addEventListener('focus', keepResultsOpen);
    }
  });

  // Keep the persistent footer link in sync with the current query.
  function syncFooter(query) {
    if (!viewAll) {
      return;
    }
    const q = query || '';
    if (q.length >= MIN_QUERY_LENGTH) {
      viewAll.href = searchPageUrl(q);
      viewAll.hidden = false;
    } else {
      viewAll.hidden = true;
    }
  }

  return {
    overlay,
    syncFooter,
    setAutocomplete( api ) {
      autocompleteApi = api;
    },
  };
}

export default function algoliaAutocomplete() {
  const container = document.querySelector('#autocomplete');
  if (!container || !config.applicationId) {
    return;
  }

  const controls = initOverlay();
  const searchClient = algoliasearch(config.applicationId, config.searchApiKey);
  const panelContainer = document.getElementById('cornish-search-panel') || undefined;

  const autocompleteApi = autocomplete({
    container: '#autocomplete',
    panelContainer,
    placeholder: config.placeholder,
    detachedMediaQuery: 'none',
    openOnFocus: false,
    onStateChange({ state }) {
      if (controls && controls.syncFooter) {
        controls.syncFooter(state.query);
      }
    },
    onSubmit({ state }) {
      if (state.query) {
        window.location.href = searchPageUrl(state.query);
      }
    },
    render({ elements, render, html }, root) {
      // "View All Results" lives in the overlay footer (always keyboard
      // reachable), so the panel only renders the results source.
      render(html`<div class="aa-PanelLayout">${elements.wp_content}</div>`, root);
    },
    getSources({ query }) {
      if (query.length < MIN_QUERY_LENGTH) {
        return [];
      }

      return [
        {
          sourceId: 'wp_content',
          getItems() {
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: config.indexName,
                  query,
                  params: {
                    filters: 'status:"publish"',
                    hitsPerPage: 8,
                  },
                },
              ],
            });
          },
          getItemUrl({ item }) {
            return item.relative_url;
          },
          templates: {
            header({ html }) {
              // Group heading ("Website") is off for now; re-enable via config.
              return config.showGroupHeadings
                ? html`<div class="results-header">${config.websiteLabel}</div>`
                : null;
            },
            item({ item, html, components }) {
              return html`<a class="search-result" href="${item.relative_url}">
                <div class="search-result__title">
                  ${components.Highlight({ hit: item, attribute: 'title' })}
                </div>
                <div class="search-result__meta">${buildKicker(item, html)}</div>
              </a>`;
            },
            noResults() {
              return config.noResultsLabel;
            },
          },
        },
      ];
    },
  });

  if (controls && controls.setAutocomplete) {
    controls.setAutocomplete(autocompleteApi);
  }
}

algoliaAutocomplete();
